import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { accountsListService } from '../services/accountsList.service';
import type { AccountFilters } from '../types/accounts.types';

export function useAccounts() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<AccountFilters>({ query: '', status: 'all' });

  const query = useQuery({
    queryKey: ['settings', 'accounts', 'list', companyId],
    queryFn: () => accountsListService.getAccounts(companyId),
    enabled: !!companyId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => accountsListService.deleteAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'accounts'] });
      toast.success('Đã xóa tài khoản');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Không thể xóa tài khoản');
    }
  });

  const accounts = useMemo(() => {
    const search = filters.query.toLowerCase().trim();
    return (query.data || []).filter(item => {
      const matchesSearch = !search || 
        item.accountName.toLowerCase().includes(search) || 
        item.accountNumber.toLowerCase().includes(search) ||
        item.bankName.toLowerCase().includes(search);
      const matchesStatus = filters.status === 'all' || item.status === filters.status;
      return matchesSearch && matchesStatus;
    });
  }, [query.data, filters]);

  return {
    accounts,
    filters,
    isLoading: query.isLoading,
    setFilters,
    deleteAccount: (id: string) => deleteMutation.mutate(id),
  };
}
